import React, { useState } from 'react'

import { Button, CardHeader } from '@mui/material'
import AddCustomerUser from 'features/customers/components/users/AddCustomerUser'
import CustomerUserActions from 'features/customers/components/users/CustomerUserActions'
import CustomerUserActiveToggle from 'features/customers/components/users/CustomerUserActiveToggle'
import CustomerUserVerified from 'features/customers/components/users/CustomerUserVerified'
import { useCustomerFeatures } from 'features/customers/hooks/useCustomerFeatures'
import { useCustomerUsers } from 'features/customers/hooks/useCustomerUsers'
import { useOutreachSeats } from 'features/customers/hooks/useOutreachSeats'
import sortBy from 'lodash/sortBy'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { capitalize } from '@albacross/frontend-utils'
import { EFeatureNames, EPermissions } from '@albacross/types'

import LoadingView from 'components/LoadingView'
import { SimpleTable } from 'components/table'

const getLink = (field: any) => (user: any) => (
  <Link to={UrlProvider.getUrl('user', { id: user.user_id })}>{user[field]}</Link>
)

const CustomerUsers = () => {
  const { data: users, isLoading: isLoadingUsers } = useCustomerUsers()
  const { data: customerFeatures, isLoading: isLoadingCustomerFeatures } = useCustomerFeatures()
  const { blockNew } = useOutreachSeats()
  const [open, setOpen] = useState(false)

  const displayOutreachLicenseConfig =
    customerFeatures[EFeatureNames.OutreachSenders] !== undefined && customerFeatures[EFeatureNames.Outreach]

  const outreachLicenseConfig = {
    title: 'Auto-Engage License',
    render: ({ user_id, role }) => {
      const hasOutreachLicense = role?.permissions?.[EPermissions.OutreachSender]
      const disabled = !hasOutreachLicense && blockNew
      return (
        <CustomerUserActiveToggle
          checked={hasOutreachLicense}
          disabled={disabled}
          disabledTooltipText={disabled ? 'No outreach licenses available' : undefined}
          isOutreachLicense
          userId={user_id}
        />
      )
    },
  }

  const config = [
    {
      title: 'First name',
      render: getLink('first_name'),
    },
    {
      title: 'Last name',
      render: getLink('last_name'),
    },
    {
      title: 'E-mail',
      render: getLink('email'),
    },
    {
      title: 'Verified',
      render: ({ user_id, verified }) => <CustomerUserVerified userId={user_id} verified={verified} />,
    },
    ...(displayOutreachLicenseConfig ? [outreachLicenseConfig] : []),
    {
      title: 'Job title',
      field: 'job_title',
    },
    {
      title: 'Job department',
      field: 'job_department',
    },
    {
      title: 'Role',
      render: ({ role }) => capitalize(role.name),
    },
    {
      title: 'Action',
      render: user => <CustomerUserActions user={user} userId={user.user_id} />,
    },
  ]

  return (
    <>
      <CardHeader action={<Button onClick={() => setOpen(true)}>Add user</Button>} />
      <LoadingView loading={isLoadingUsers || isLoadingCustomerFeatures}>
        <SimpleTable config={config} data={sortBy(users, 'email')} striped />
      </LoadingView>
      {open && <AddCustomerUser onClose={() => setOpen(false)} open={open} />}
    </>
  )
}

export default CustomerUsers
